import { useState } from "react";
import type { OrderResponse } from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { describeError } from "./States";
import { formatDateTime } from "../lib/format";

/**
 * Seller guidance + buyer/seller "check delivery now" for an NFT_TRANSFER order — see
 * MARKETPLACE_EXPANSION_PLAN.md's Phase 5a' and SECURITY.md's non-custodial invariant. Simpler
 * even than DomainTransferPanel: nothing is ever submitted or revealed through this platform. The
 * seller sends the NFT directly to the address shown below using their own wallet app or
 * marketplace; either party can check whether that's actually happened yet.
 */

// Literal keys (not Record<string, ...>) so `STAGE_COPY.AWAITING_TRANSFER` is known to exist under
// noUncheckedIndexedAccess, which the fallback below relies on.
const STAGE_COPY: Record<"AWAITING_TRANSFER" | "DELIVERED", { title: string; detail: string }> = {
  AWAITING_TRANSFER: {
    title: "Waiting on the seller",
    detail:
      "The seller needs to send the NFT directly to the address below, using their own wallet " +
      "app or a marketplace like OpenSea — never to MarketOnBase itself.",
  },
  DELIVERED: {
    title: "Delivery confirmed on-chain",
    detail:
      "An on-chain check confirmed the NFT is now in the buyer's wallet. Buyer: once you've " +
      "confirmed it's the right item, release funds below.",
  },
};

export function NftTransferPanel({
  order,
  isBuyer,
  isSeller,
  onOrderChanged,
}: {
  order: OrderResponse;
  isBuyer: boolean;
  isSeller: boolean;
  onOrderChanged: () => void;
}) {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ delivered: boolean; checkedAt: string } | null>(
    null,
  );

  const stage = order.nftTransferStage;
  if (!stage) return null;
  const copy = STAGE_COPY[stage as keyof typeof STAGE_COPY] ?? STAGE_COPY.AWAITING_TRANSFER;

  async function handleCheck() {
    setCheckError(null);
    setIsChecking(true);
    try {
      const result = await apiClient.checkNftDelivery(order.id);
      setLastResult(result);
      if (result.delivered) onOrderChanged();
    } catch (err) {
      setCheckError(describeError(err));
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <div className="card stack">
      <h2>NFT handoff</h2>
      <div className="alert alert--info" role="status">
        <strong>{copy.title}</strong>
        <p>{copy.detail}</p>
      </div>

      {isSeller && stage === "AWAITING_TRANSFER" && order.nftDeliveryAddress && (
        <div className="stack">
          <div className="row row--between">
            <span className="muted text-sm">Send the NFT to</span>
            <span className="mono">{order.nftDeliveryAddress}</span>
          </div>
          <p className="text-sm muted">
            This is the buyer's own wallet — the exact address that funded this order.
            Sending it anywhere else (including back to MarketOnBase) will not complete this
            order.
          </p>
        </div>
      )}

      {stage !== "DELIVERED" && (
        <div className="row">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={isChecking}
            onClick={() => void handleCheck()}
          >
            {isChecking ? "Checking…" : "Check delivery now"}
          </button>
        </div>
      )}

      {checkError && (
        <p className="field-error-text" role="alert">
          {checkError}
        </p>
      )}

      {lastResult && !lastResult.delivered && (
        <p className="text-sm muted">
          Not delivered yet as of {formatDateTime(lastResult.checkedAt)} — it's fine to check again
          later.
        </p>
      )}

      {order.nftDeliveryCheckedAt && stage === "AWAITING_TRANSFER" && !lastResult && (
        <p className="text-sm muted">
          Last checked {formatDateTime(order.nftDeliveryCheckedAt)} — not delivered yet.
        </p>
      )}

      {isBuyer && stage === "DELIVERED" && (
        <p className="text-sm muted">
          Confirm the token in your own wallet matches what you bought before releasing funds —
          this check only confirms ownership moved, not that it's the exact item you expected.
        </p>
      )}
    </div>
  );
}
